import { FC, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store';
import { FetchState } from '../../../store/fetchState';
import { getOptionsSupplier, selectOptionsSupplier, selectOptionsSupplierState } from '../../../store/entities/optionsSupplier';
import { QcsAutocomplete } from './QcsAutocomplete';
import { CancelToken } from 'axios';
import { useTranslation } from 'react-i18next';
import { getLangNameObj } from '../../../utils/format';
import { SUPPLIER_VISIT_FAKE_ID } from '../../../utils/constants';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { SupplierDto } from '@api/models/suppliers/SupplierDto';

interface Props {
  multiple?: boolean;
  name: string;
  label?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  allowVisit?: boolean;
  canLendEmployees?: boolean;
  filterSuppliers?: SupplierDto[] | null;
  onChange?: (value: ReferenceDto | ReferenceDto[] | null) => void;
}

export const SupplierAutocomplete: FC<Props> = ({
  multiple,
  name,
  label,
  disabled,
  required,
  allowVisit,
  canLendEmployees,
  filterSuppliers,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const optionsSupplier = useAppSelector(selectOptionsSupplier);
  const optionsSupplierState = useAppSelector(selectOptionsSupplierState);
  const [searchValue, setSearchValue] = useState<string>('');

  const loadOptions = (search: string | undefined, cancelToken: CancelToken) => {
    setSearchValue(search || '');
    dispatch(getOptionsSupplier(search, cancelToken, canLendEmployees));
  };

  const data = useMemo(() => {
    let suppliers = optionsSupplier;
    let shouldShowVisit = allowVisit;

    // Filter by specific suppliers only when filterSuppliers is provided AND search is empty
    if (filterSuppliers && filterSuppliers.length > 0 && !searchValue) {
      const filterIds = filterSuppliers.map((s) => s.id);
      suppliers = optionsSupplier.filter((s) => filterIds.includes(s.id));
      shouldShowVisit = false;
    }

    if (shouldShowVisit) {
      return [
        {
          id: SUPPLIER_VISIT_FAKE_ID,
          name: t('invitationTypeVisit.detailTitle'),
        },
        ...suppliers,
      ];
    }

    return suppliers;
  }, [optionsSupplier, allowVisit, filterSuppliers, searchValue, t]);

  return (
    <QcsAutocomplete
      multiple={multiple}
      name={name}
      label={label}
      required={required}
      disabled={disabled}
      options={data}
      loading={optionsSupplierState === FetchState.Loading}
      loadingError={optionsSupplierState === FetchState.Error}
      loadOptions={loadOptions}
      getOptionId={(option: ReferenceDto) => option.id!}
      getOptionName={(option: ReferenceDto) => getLangNameObj(i18n, option)}
      onChange={onChange}
    />
  );
};
